import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { NgRedux } from '@angular-redux/store';
import { TranslateService } from '@ngx-translate/core';

import { UUID } from 'angular2-uuid';
import { throwError as observableThrowError, BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { catchError, delay, filter, finalize, retryWhen, shareReplay, switchMap, take } from 'rxjs/operators';

import { AuthService, USE_ANONYMOUS_ACCESS_TOKEN } from './auth.service';
import { LogService } from './log.service';
import { DeviceIdService } from './device-id.service';
import { NetworkService } from './network.service';
import { jwtDecode } from 'jwt-decode';

@Injectable({
    providedIn: 'root'
})
export class AuthInterceptorService implements HttpInterceptor {
    isRefreshingToken = false;
    isRefreshingDeviceToken = false;
    private refreshTokenInFlight$: Observable<string> | null = null;
    deviceTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    constructor(
        @Inject('ENV') private env: any,
        private auth: AuthService,
        private router: Router,
        private ls: LogService,
        private translate: TranslateService,
        private store: NgRedux<any>,
        private deviceIdService: DeviceIdService,
        private network: NetworkService
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (req.url.includes('/auth/refresh')) {
            return next.handle(req);
        }

        if (this.network && this.network.getCurrentStatus() === false) {
            return next.handle(req);
        }

        // Check if the endpoint requires a token
        const isProtectedEndpoint =
            this.env.AFC_TOKEN_ENDPOINTS?.some((value) => req.url.includes(value)) ||
            this.env.DEVICE_AND_USER_DUPLICATE_ENDPOINTS?.some((value) => req.url.includes(value)) ||
            this.env.DEVICE_AND_USER_PRIORITY_ENDPOINTS?.some((value) => req.url.includes(value));

        // check if anonymous user access token must be used
        const useAnonymousAccessToken = isProtectedEndpoint && req.context.get(USE_ANONYMOUS_ACCESS_TOKEN);

        // determine if this is a device based request
        const userToken = localStorage.getItem(`${this.env.APP_NAME}-access`);
        const deviceIdToken = this.deviceIdService.getAccessDeviceToken();
        let token = userToken;

        const callWithDeviceIdTokenOnly =
            deviceIdToken &&
            ((!userToken && this.env.DEVICE_AND_USER_PRIORITY_ENDPOINTS?.some((value) => req.url.includes(value))) ||
                req.params.get('deviceTokenOnly'));

        const isDevice =
            this.env.DEVICE_AND_USER_DUPLICATE_ENDPOINTS?.some((value) => req.url.includes(value)) ||
            callWithDeviceIdTokenOnly ||
            useAnonymousAccessToken;

        if (callWithDeviceIdTokenOnly) {
            token = this.deviceIdService.getAccessDeviceToken();
        }

        if (useAnonymousAccessToken) {
            token = this.deviceIdService.getAccessDeviceToken();
        }
        // request origins can provide token in Authorization header in specific cases (e.g. device based requests)
        // then we should use that token instead of the one from local storage
        if (req.headers.get('Authorization')) {
            token = req.headers.get('Authorization').replace('Bearer ', '');
        }

        if (isProtectedEndpoint) {
            if (!token) {
                if (isDevice) {
                    return this.deviceIdService.refreshAccessDeviceToken().pipe(
                        switchMap((newToken) => {
                            if (!newToken) {
                                return observableThrowError('Failed to get device token');
                            }
                            return next.handle(this.addToken(req, newToken));
                        }),
                        catchError((error) => this.handleRequestError(error, req, next, isDevice))
                    );
                } else {
                    return this.auth.refreshToken().pipe(
                        switchMap((data: { token: string }) => {
                            if (!data || !data.token) {
                                return observableThrowError('Failed to get user token');
                            }
                            this.auth.setLocalAccessToken(data, this.env.IS_CORPORATE);
                            return next.handle(this.addToken(req, data.token));
                        }),
                        catchError((error) => {
                            return this.handleRequestError(error, req, next, isDevice);
                        })
                    );
                }
            }

            return this.refreshTokenIfNeeded(token).pipe(
                switchMap((newToken) => {
                    return next
                        .handle(this.addToken(req, newToken))
                        .pipe(catchError((error) => this.handleRequestError(error, req, next, isDevice)));
                })
            );
        }

        return next
            .handle(this.addToken(req, token))
            .pipe(catchError((error) => this.handleRequestError(error, req, next, isDevice)));
    }

    private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
        if (token) {
            return req.clone({ setHeaders: { Authorization: 'Bearer ' + token } });
        }
        return req;
    }

    private isTokenExpired(token: string): boolean {
        const expiration = jwtDecode(token).exp * 1000;
        const timeSkew = 30_000;
        return Date.now() >= expiration - timeSkew;
    }

    private refreshTokenIfNeeded(token: string): Observable<string> {
        if (this.isTokenExpired(token)) {
            return this.getOrCreateRefreshTokenInFlight$().pipe(
                catchError((error) => {
                    this.logoutUser();
                    return observableThrowError(error);
                })
            );
        }
        return of(token);
    }

    private getOrCreateRefreshTokenInFlight$(): Observable<string> {
        if (this.refreshTokenInFlight$ === null) {
            this.isRefreshingToken = true;
            this.refreshTokenInFlight$ = this.auth.refreshToken().pipe(
                switchMap((data: any) => {
                    const token = data?.token;
                    if (!token) {
                        return throwError('Received null data');
                    }

                    this.auth.setLocalAccessToken(data, this.env.IS_CORPORATE);
                    return of(token);
                }),
                shareReplay(1),
                finalize(() => {
                    this.isRefreshingToken = false;
                    this.refreshTokenInFlight$ = null;
                })
            );
        }

        return this.refreshTokenInFlight$;
    }

    private handleRequestError(
        error: any,
        req: HttpRequest<any>,
        next: HttpHandler,
        isDevice: boolean
    ): Observable<any> {
        if (req.url.includes('/auth/refresh')) {
            return observableThrowError(error);
        }

        if (error instanceof HttpErrorResponse) {
            this.ls.log(this, 'intercept', (<HttpErrorResponse>error).status);
            switch ((<HttpErrorResponse>error).status) {
                case 0:
                    // offline mode, eat the error and continue
                    return next.handle(req);
                case 400:
                    if (
                        (<HttpErrorResponse>error).error.error === 'invalid_grant' &&
                        (<HttpErrorResponse>error).error?.error_description === 'User disabled'
                    ) {
                        this.router.navigate([this.translate.currentLang, 'explore']);
                        return;
                    }
                    // refresh failed can result in 400 so need to handle that separately
                    if (!isDevice && this.isRefreshingToken) {
                        return this.handleUnauthorized(req, next);
                    }

                    if (isDevice && this.isRefreshingDeviceToken) {
                        return this.handleUnauthorizedWithDeviceToken(req, next);
                    }
                    if (isDevice) {
                        if (
                            (<HttpErrorResponse>error).error.error === 'invalid_grant' &&
                            (<HttpErrorResponse>error).error?.error_description === 'User disabled'
                        ) {
                            this.router.navigate([this.translate.currentLang, 'explore']);
                            return;
                        }
                    }
                    return observableThrowError(error);
                case 401: {
                    if (this.env.DEEP_LINKING && error.url && error.url.includes('signup/verify')) {
                        return observableThrowError(error);
                    }

                    if (isDevice) {
                        return this.handleUnauthorizedWithDeviceToken(req, next);
                    }
                    return this.handleUnauthorized(req, next);
                }
                case 404:
                    return observableThrowError(error);
                case 409:
                    // refresh failed can result in 409 so need to handle that separately
                    if (!isDevice && this.isRefreshingToken) {
                        this.logoutUser();
                        return this.handleUnauthorized(req, next);
                    }
                    if (isDevice && this.isRefreshingDeviceToken) {
                        this.logoutUser();
                        return this.handleUnauthorizedWithDeviceToken(req, next);
                    }

                    return observableThrowError(error);
                default:
                    return observableThrowError(error);
            }
        } else {
            return observableThrowError(error);
        }
    }

    handleUnauthorized(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
        if (this.env.IS_NEW_AFC) {
            return;
        }
        const userToken = localStorage.getItem(`${this.env.APP_NAME}-access`);

        if (!userToken) {
            return observableThrowError(new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' }));
        }

        return this.getOrCreateRefreshTokenInFlight$().pipe(
            switchMap((token) => {
                this.ls.log(this, 'handleUnauthorized', token);
                return next.handle(this.addToken(req, token));
            }),
            retryWhen((errors) =>
                errors.pipe(
                    switchMap((error) => {
                        if (error === 'Received null data') {
                            return of(error);
                        }
                        return throwError(error);
                    }),
                    delay(1000),
                    take(3)
                )
            ),
            catchError((error) => {
                if (
                    error instanceof HttpErrorResponse &&
                    error.status === 400 &&
                    error.url?.includes('/auth/refresh')
                ) {
                    localStorage.removeItem(`${this.env.APP_NAME}-access`);
                    localStorage.removeItem(`${this.env.APP_NAME}-access-user`);
                    localStorage.removeItem(`${this.env.APP_NAME}-access-user-email`);
                    return observableThrowError(new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' }));
                }
                this.logoutUser();
                return observableThrowError(error);
            })
        );
    }

    handleUnauthorizedWithDeviceToken(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
        if (!this.isRefreshingDeviceToken) {
            this.isRefreshingDeviceToken = true;

            // Reset here so that the following requests wait until the token
            // comes back from the refreshToken call.
            this.deviceTokenSubject.next(null);
            // get a new token via auth.refreshToken
            return this.deviceIdService.refreshAccessDeviceToken().pipe(
                switchMap((token: any) => {
                    // did we get a new token retry previous request
                    if (token) {
                        // this.auth.setLocalAccessToken(data, this.env.IS_CORPORATE);
                        this.deviceTokenSubject.next(token);
                        this.isRefreshingDeviceToken = false;
                        const clonedRequestWithDeviceIdToken = req.clone({
                            setHeaders: {
                                Authorization: `Bearer ${token}`
                            }
                        });
                        return next.handle(clonedRequestWithDeviceIdToken);
                    }

                    // what should  we do if error in process of refreshing
                    return observableThrowError('');
                }),
                catchError((error) => {
                    // If there is an exception calling 'refreshToken', bad news so logout.
                    this.isRefreshingDeviceToken = false;

                    return observableThrowError(error);
                }),
                finalize(() => {
                    this.isRefreshingDeviceToken = false;
                })
            );
        }
        return this.deviceTokenSubject.pipe(
            filter((token) => token != null),
            take(1),
            switchMap((token) => {
                const clonedRequestWithDeviceIdToken = req.clone({
                    setHeaders: {
                        Authorization: `Bearer ${token}`
                    }
                });
                return next.handle(clonedRequestWithDeviceIdToken);
            })
        );
    }

    logoutUser() {
        // this is not good, we should start using auth under /components and listen to auth events in applications
        // and react to those events.
        setTimeout(() => {
            this.auth.signOut();
            const alertConfig = {
                id: UUID.UUID(),
                type: 'Error',
                message: 'TOKEN_ERR',
                duration: 100000,
                showAfterRouteChange: true
            };
            this.store.dispatch({
                type: '[Notification]: Add alert',
                payload: alertConfig
            });

            this.router.navigate([this.translate.currentLang, 'login']);
        }, 1200);
        return observableThrowError('');
    }
}
